import type { ReactNode } from "react";
import { diaDaSemanaCurto, formatarDataCurta } from "../../util/datas";
import { cx } from "./cx";
import { Etiqueta, type TomEtiqueta } from "./Etiqueta";
import type { Urgencia } from "./Prazo";

export type TarefaResumo = {
  id: string;
  titulo: string;
  detalhe?: string | null;
  cliente?: string | null;
  numeroProcesso?: string | null;
  execucao?: string | null;
  fatal?: string | null;
  urgencia?: Urgencia;
  etiqueta?: { texto: string; tom: TomEtiqueta } | null;
  area?: string | null;
  responsavel?: string | null;
  audiencia?: boolean;
};

/*
 * Visão geral das tarefas (tela inicial; decisão do Henrique em 21/10): uma tabela, sem agrupar por dia, com
 * EXECUÇÃO e PRAZO FATAL em colunas próprias. Sem número do processo (aparece no painel). Uma linha de 36 px
 * quando há espaço; duas no celular ou ao lado do painel (container query).
 */

const COLUNAS = "@2xl:grid-cols-[1.5rem_minmax(0,2fr)_minmax(0,1.2fr)_5.5rem_5.5rem_9rem]";
const COLUNAS_GESTOR = "@4xl:grid-cols-[1.5rem_minmax(0,2fr)_minmax(0,1.2fr)_5.5rem_5.5rem_9rem_minmax(0,9rem)]";

export function ListaTarefas({ children, comResponsavel }: { children: ReactNode; comResponsavel?: boolean }) {
  return (
    <div className="@container" role="table" aria-label="Tarefas">
      <div role="row" className={cx("hidden h-8 items-center gap-x-3 fio-b text-meta font-medium text-texto-3 @2xl:grid", COLUNAS, comResponsavel && COLUNAS_GESTOR)}>
        <span role="columnheader"><span className="sr-only">Selecionar</span></span>
        <span role="columnheader">Tarefa</span>
        <span role="columnheader">Cliente</span>
        <span role="columnheader">Execução</span>
        <span role="columnheader">Prazo fatal</span>
        <span role="columnheader">Situação</span>
        {comResponsavel && <span role="columnheader" className="hidden @4xl:block">Responsável</span>}
      </div>
      <div role="rowgroup" className="entre-fios">{children}</div>
    </div>
  );
}

function Execucao({ data }: { data?: string | null }) {
  if (!data) return <span className="text-meta text-texto-3">sem data</span>;
  return <span className="text-texto-1">{diaDaSemanaCurto(data)} <span className="numeros">{formatarDataCurta(data)}</span></span>;
}

function Fatal({ data, urgencia = "normal" }: { data?: string | null; urgencia?: Urgencia }) {
  if (!data) return <span className="text-meta text-texto-3">sem fatal</span>;
  return (
    <span className={cx(urgencia === "normal" ? "text-texto-2" : "font-medium text-alerta")}
      title={urgencia === "vencido" ? "Prazo fatal vencido" : urgencia === "perto" ? "Prazo fatal próximo" : "Prazo fatal"}>
      <span className="numeros">{formatarDataCurta(data)}</span>
      {urgencia === "vencido" && <span className="text-meta"> venceu</span>}
    </span>
  );
}

export function LinhaTarefa({ t, selecionada, aoSelecionar, aoAbrir, ativa, comResponsavel }: {
  t: TarefaResumo; selecionada?: boolean; aoSelecionar?: (marcar: boolean) => void; aoAbrir?: () => void;
  ativa?: boolean; comResponsavel?: boolean;
}) {
  return (
    <div role="row" className={cx("group relative grid items-center gap-x-3 py-2 hover:bg-superficie-2",
      "grid-cols-[1.5rem_minmax(0,1fr)_auto]", COLUNAS, comResponsavel && COLUNAS_GESTOR, ativa && "bg-superficie-2")}>
      {ativa && <span aria-hidden className="absolute inset-y-0 -left-2 w-0.5 bg-acao" />}
      <span role="cell" className="relative z-10 grid size-6 place-items-center">
        <input type="checkbox" className="size-4 cursor-pointer" checked={!!selecionada}
          onChange={(e) => aoSelecionar?.(e.target.checked)} aria-label={`Selecionar ${t.titulo}`} />
      </span>

      <span role="cell" className="min-w-0">
        <button type="button" onClick={aoAbrir} title={t.detalhe ?? undefined}
          className="flex w-full min-w-0 cursor-pointer items-center gap-2 text-left after:absolute after:inset-0 after:content-['']">
          {t.audiencia && <span aria-hidden className="size-2 shrink-0 rounded-pilula bg-conferir" title="Audiência" />}
          <span className="truncate font-medium text-texto-1">{t.titulo}</span>
        </button>
      </span>

      {/* Celular: situação à direita do título; a 2ª linha junta cliente e as duas datas, com rótulo. */}
      <span className="col-start-3 row-start-1 @2xl:hidden">{t.etiqueta && <Etiqueta tom={t.etiqueta.tom}>{t.etiqueta.texto}</Etiqueta>}</span>
      <span className="col-span-2 col-start-2 flex min-w-0 flex-wrap items-baseline gap-x-3 text-meta text-texto-3 @2xl:hidden">
        <span className="max-w-full truncate text-corpo text-texto-2">{t.cliente ?? "sem cliente"}</span>
        {t.execucao ? <span>exec. <Execucao data={t.execucao} /></span> : <span>sem data</span>}
        {t.fatal ? <span>fatal <Fatal data={t.fatal} urgencia={t.urgencia} /></span> : <span>sem fatal</span>}
      </span>

      <span role="cell" className="hidden truncate text-texto-2 @2xl:block">{t.cliente ?? <span className="text-texto-3">sem cliente</span>}</span>
      <span role="cell" className="hidden @2xl:block"><Execucao data={t.execucao} /></span>
      <span role="cell" className="hidden @2xl:block"><Fatal data={t.fatal} urgencia={t.urgencia} /></span>
      <span role="cell" className="hidden min-w-0 @2xl:block">{t.etiqueta && <Etiqueta tom={t.etiqueta.tom}>{t.etiqueta.texto}</Etiqueta>}</span>
      {comResponsavel && <span role="cell" className="hidden truncate text-texto-2 @4xl:block">{t.responsavel}</span>}
    </div>
  );
}
